import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Img, NoData } from 'ui-kit';
import { ARROW_DOWN, ARROW_UP } from 'assets/icons';
import * as Styled from './styles';

const DEBOUNCE_DELAY = 300;

const CollapsibleTable = ({ title, isExpanded, onToggle, columns, rows }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceTimer = useRef(null);

  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(debounceTimer.current);
  }, [searchQuery]);

  const filteredRows = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [0, 1].some((colIdx) =>
        String(row[colIdx] ?? '')
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [rows, debouncedQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    setSearchQuery(pasted.trim());
  };

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !isExpanded) {
        onToggle();
      }
    },
    [isExpanded, onToggle],
  );

  return (
    <>
      <Styled.SectionTitleWrapper onClick={onToggle}>
        <Styled.SectionTitle>
          {title}
          {` ${filteredRows.length} items`}
        </Styled.SectionTitle>
        <Styled.SearchWrapper>
          <Styled.SearchInput
            placeholder="Search..."
            value={searchQuery}
            onClick={(e) => e.stopPropagation()}
            onChange={handleSearchChange}
            onPaste={handleSearchPaste}
            onKeyDown={handleSearchKeyDown}
          />
        </Styled.SearchWrapper>
        <Styled.ExpandIcon>
          <Styled.InfoWrapper>
            <Img
              tabIndex={0}
              size={[15, 15]}
              src={isExpanded ? ARROW_UP : ARROW_DOWN}
              aria-label="user_information"
              alt="user_information"
            />
          </Styled.InfoWrapper>
        </Styled.ExpandIcon>
      </Styled.SectionTitleWrapper>
      {isExpanded && (
        <Styled.TableWrapper>
          <Styled.Table>
            <Styled.TableHeader>
              <Styled.TableRow>
                {columns.map((col) => (
                  <Styled.TableHeaderCell key={col}>{col}</Styled.TableHeaderCell>
                ))}
              </Styled.TableRow>
            </Styled.TableHeader>
            <Styled.TableBody>
              {filteredRows.length > 0 ? (
                filteredRows.map((cells, index) => (
                  <Styled.TableRow key={index}>
                    {cells.map((cell, cellIndex) => (
                      <Styled.TableCell key={cellIndex}>{cell}</Styled.TableCell>
                    ))}
                  </Styled.TableRow>
                ))
              ) : (
                // A real row and cell spanning every column: a bare <div> in <tbody> is laid out as a
                // stray cell the width of the first column, which pushed the message off to the side.
                <Styled.TableRow>
                  <Styled.EmptyCell colSpan={columns.length}>
                    <Styled.NoDataWrapper>
                      <NoData descriptionText="No data" />
                    </Styled.NoDataWrapper>
                  </Styled.EmptyCell>
                </Styled.TableRow>
              )}
            </Styled.TableBody>
          </Styled.Table>
        </Styled.TableWrapper>
      )}
    </>
  );
};

CollapsibleTable.propTypes = {
  title: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.array).isRequired,
};

export default CollapsibleTable;
